import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { formatPlate } from "../../utility/funcs";
import {
  AppProvider,
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  InlineStack,
  Spinner,
  Text,
  TextField,
} from "@shopify/polaris";
import esTranslations from "@shopify/polaris/locales/es.json";
import { CameraIcon, ImageIcon } from "@shopify/polaris-icons";
import "./FieldCapture.css";
import { makeCaptureClient, compressImage, PickPhoto, Thumb } from "./captureKit";

// Public page a service provider opens on their phone from the link staff
// sent them (/captura/:token). No login: the token is the credential, and all
// reads and writes go through the service_capture edge function — the page
// never talks to Supabase tables or storage directly.

const callCapture = makeCaptureClient("service_capture");

function FieldCapture() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [photos, setPhotos] = useState([]);
  const [readings, setReadings] = useState({ plate: null, odometer: null });
  const [reading, setReading] = useState({ plate: false, odometer: false });
  const [odometer, setOdometer] = useState("");
  // Nothing is pre-selected, not even what staff requested: the provider has
  // to state what they actually did.
  const [doneCats, setDoneCats] = useState([]);
  const [catNotes, setCatNotes] = useState({});
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await callCapture({ action: "load", token });
      setState({ loading: false, data });
      setPhotos(data.photos || []);
      const l = data.link;
      setReadings({
        plate: l.plate_ai ? { value: l.plate_ai, matches: l.plate_matches } : null,
        odometer: l.odometer_ai ? { km: l.odometer_ai } : null,
      });
      setOdometer(
        l.odometer_entered ? String(l.odometer_entered) : l.odometer_ai ? String(l.odometer_ai) : "",
      );
      setDoneCats(l.done_categories || []);
      setCatNotes(l.category_notes || {});
      setNotes(l.provider_notes || "");
    } catch (err) {
      setState({ loading: false, error: err.message });
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const data = state.data;
  const editable = !!data?.editable && !done;
  // Requested categories first, then the rest of the catalog in its order.
  const categories = useMemo(() => {
    const all = data?.categories || [];
    const requested = new Set(data?.link?.expected_categories || []);
    return [
      ...all.filter((c) => requested.has(c.key)),
      ...all.filter((c) => !requested.has(c.key)),
    ];
  }, [data]);

  const upload = async (file, meta) => {
    const tempId = `tmp-${Date.now()}-${Math.random()}`;
    const preview = URL.createObjectURL(file);
    const single = meta.kind === "plate" || meta.kind === "odometer";
    setPhotos((prev) => [
      ...(single ? prev.filter((p) => p.kind !== meta.kind) : prev),
      { ...meta, path: tempId, preview, uploading: true },
    ]);
    if (single) setReading((r) => ({ ...r, [meta.kind]: true }));

    try {
      const blob = await compressImage(file);
      const form = new FormData();
      form.append("action", "upload");
      form.append("token", token);
      form.append("kind", meta.kind);
      if (meta.category) form.append("category", meta.category);
      if (meta.shot) form.append("shot", meta.shot);
      form.append("file", blob, "foto.jpg");
      const res = await callCapture(form);
      setPhotos((prev) =>
        prev.map((p) => (p.path === tempId ? { ...res.photo, preview } : p)),
      );
      if (meta.kind === "plate") setReadings((r) => ({ ...r, plate: res.reading }));
      if (meta.kind === "odometer") {
        setReadings((r) => ({ ...r, odometer: res.reading }));
        // Pre-fill, but never overwrite what the provider already typed.
        if (res.reading?.km) setOdometer((cur) => cur || String(res.reading.km));
      }
    } catch (err) {
      setPhotos((prev) =>
        prev.map((p) =>
          p.path === tempId ? { ...p, uploading: false, failed: err.message } : p,
        ),
      );
    } finally {
      if (single) setReading((r) => ({ ...r, [meta.kind]: false }));
    }
  };

  const removePhoto = async (photo) => {
    if (photo.failed) {
      setPhotos((prev) => prev.filter((p) => p !== photo));
      return;
    }
    const before = photos;
    setPhotos((prev) => prev.filter((p) => p.path !== photo.path));
    try {
      await callCapture({ action: "delete_photo", token, path: photo.path });
      if (photo.kind === "plate") setReadings((r) => ({ ...r, plate: null }));
      if (photo.kind === "odometer") setReadings((r) => ({ ...r, odometer: null }));
    } catch (err) {
      setPhotos(before);
      alert(err.message);
    }
  };

  const toggleCat = (key) =>
    setDoneCats((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key],
    );

  const plateReady = photos.some((p) => p.kind === "plate" && !p.uploading && !p.failed);
  const odoReady = photos.some((p) => p.kind === "odometer" && !p.uploading && !p.failed);
  const anyUploading = photos.some((p) => p.uploading);
  const shotPhotos = (catKey, shotKey) =>
    photos.filter(
      (p) => p.kind === "evidence" && p.category === catKey && p.shot === shotKey,
    );
  const doneCatObjs = categories.filter((c) => doneCats.includes(c.key));
  // Per category: every required shot has a photo, and the note if it asks one.
  const catMissing = (c) => [
    ...c.shots
      .filter((sh) => sh.required)
      .filter((sh) => !shotPhotos(c.key, sh.key).some((p) => !p.uploading && !p.failed))
      .map((sh) => sh.label),
    ...(c.requires_note && !(catNotes[c.key] || "").trim() ? ["descripción"] : []),
  ];
  const evidenceReady =
    doneCatObjs.length > 0 && doneCatObjs.every((c) => catMissing(c).length === 0);
  const missing = [
    !plateReady && "foto de la placa",
    !odoReady && "foto del odómetro",
    !(Number(odometer) > 0) && "kilometraje",
    !doneCats.length && "servicios realizados",
    ...doneCatObjs
      .filter((c) => catMissing(c).length)
      .map((c) => `${c.label} (${catMissing(c).join(", ")})`),
  ].filter(Boolean);

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await callCapture({
        action: "submit",
        token,
        odometer: Number(odometer),
        done_categories: doneCats,
        category_notes: catNotes,
        notes,
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ------------------------------------------------------------------------
  const shell = (content) => (
    <AppProvider i18n={esTranslations}>
      <div className="fc-page">
        <div className="fc-header">
          <img src="/images/logo_white_256.webp" alt="GoMove" />
          <span className="fc-header-title">Registro de servicio</span>
        </div>
        <div className="fc-body">{content}</div>
      </div>
    </AppProvider>
  );

  if (state.loading)
    return shell(
      <div className="fc-center">
        <Spinner />
      </div>,
    );

  if (state.error)
    return shell(
      <Banner tone="critical" title="No se pudo abrir el servicio">
        <p>{state.error}</p>
      </Banner>,
    );

  const car = data.service.car || {};
  const carTitle = [car.model?.brand?.name, car.model?.name, car.year]
    .filter(Boolean)
    .join(" ");

  const carCard = (
    <Card>
      <InlineStack gap="300" blockAlign="center" wrap={false}>
        {car.model?.image_url && (
          <img className="fc-car-img" src={car.model.image_url} alt="" />
        )}
        <BlockStack gap="100">
          <Text variant="headingMd" as="h2">
            {carTitle || "Vehículo"}
          </Text>
          <InlineStack gap="200">
            <span className="fc-plate">{car.plate || "—"}</span>
            {car.color && <Text tone="subdued">{car.color}</Text>}
          </InlineStack>
          <Text tone="subdued" variant="bodySm">
            {data.service.provider?.name} · Servicio #{data.service.id}
          </Text>
        </BlockStack>
      </InlineStack>
    </Card>
  );

  if (done || data.link.submitted_at)
    return shell(
      <BlockStack gap="400">
        {carCard}
        <Banner tone="success" title="¡Gracias! El servicio fue enviado.">
          <p>
            GoMove ya recibió la evidencia. Cuando tengas la factura, envíala
            por el medio de siempre.
          </p>
        </Banner>
      </BlockStack>,
    );

  if (!editable)
    return shell(
      <BlockStack gap="400">
        {carCard}
        <Banner tone="warning" title="Esta liga ya no acepta cambios">
          <p>
            {data.expired
              ? "La liga expiró. Pide una nueva a GoMove."
              : "Este servicio ya no está abierto para captura."}
          </p>
        </Banner>
      </BlockStack>,
    );

  const plateReading = readings.plate;
  const odoReading = readings.odometer;
  const plateThumb = photos.find((p) => p.kind === "plate");
  const odoThumb = photos.find((p) => p.kind === "odometer");

  const expected = new Set(data.link.expected_categories || []);

  const step = (n, title, ok, children) => (
    <Card>
      <BlockStack gap="300">
        <InlineStack gap="200" blockAlign="center">
          <span className={`fc-step ${ok ? "fc-step-ok" : ""}`}>{ok ? "✓" : n}</span>
          <Text variant="headingMd" as="h3">
            {title}
          </Text>
        </InlineStack>
        {children}
      </BlockStack>
    </Card>
  );

  return shell(
    <BlockStack gap="400">
      {carCard}

      {data.link.instructions && (
        <Banner tone="info" title="Indicaciones de GoMove">
          <p style={{ whiteSpace: "pre-wrap" }}>{data.link.instructions}</p>
        </Banner>
      )}

      {step(
        1,
        "Foto de la placa",
        plateReady,
        <BlockStack gap="300">
          <Text tone="subdued">
            Toma la foto de frente, que se lean todos los caracteres.
          </Text>
          {plateThumb && (
            <div className="fc-thumbs">
              <Thumb photo={plateThumb} onDelete={removePhoto} />
            </div>
          )}
          {reading.plate && (
            <InlineStack gap="200" blockAlign="center">
              <Spinner size="small" />
              <Text tone="subdued">Leyendo placa…</Text>
            </InlineStack>
          )}
          {!reading.plate && plateThumb && !plateThumb.uploading && (
            plateReading?.value ? (
              plateReading.matches ? (
                <Banner tone="success">
                  Placa {plateReading.value} — coincide con el vehículo.
                </Banner>
              ) : (
                <Banner tone="warning">
                  Leímos <b>{plateReading.value}</b> pero el vehículo asignado es{" "}
                  <b>{formatPlate(car.plate)}</b>. Verifica que sea la unidad correcta o
                  repite la foto.
                </Banner>
              )
            ) : (
              <Banner tone="info">
                No pudimos leer la placa automáticamente. Si la foto es clara,
                puedes continuar.
              </Banner>
            )
          )}
          <PickPhoto
            icon={CameraIcon}
            variant={plateThumb ? "secondary" : "primary"}
            size="large"
            fullWidth
            onFiles={([f]) => upload(f, { kind: "plate" })}
          >
            {plateThumb ? "Repetir foto" : "Tomar foto de la placa"}
          </PickPhoto>
        </BlockStack>,
      )}

      {step(
        2,
        "Odómetro",
        odoReady && Number(odometer) > 0,
        <BlockStack gap="300">
          <Text tone="subdued">
            Con el motor encendido, que se vea el kilometraje total.
          </Text>
          {odoThumb && (
            <div className="fc-thumbs">
              <Thumb photo={odoThumb} onDelete={removePhoto} />
            </div>
          )}
          {reading.odometer && (
            <InlineStack gap="200" blockAlign="center">
              <Spinner size="small" />
              <Text tone="subdued">Leyendo odómetro…</Text>
            </InlineStack>
          )}
          <PickPhoto
            icon={CameraIcon}
            variant={odoThumb ? "secondary" : "primary"}
            size="large"
            fullWidth
            onFiles={([f]) => upload(f, { kind: "odometer" })}
          >
            {odoThumb ? "Repetir foto" : "Tomar foto del odómetro"}
          </PickPhoto>
          <TextField
            label="Kilometraje"
            type="number"
            inputMode="numeric"
            suffix="km"
            autoComplete="off"
            value={odometer}
            onChange={(v) => setOdometer(v.replace(/\D/g, ""))}
            helpText={
              odoReading?.km
                ? `Leído de la foto: ${Number(odoReading.km).toLocaleString("es-MX")} km. Confirma que sea correcto.`
                : undefined
            }
          />
        </BlockStack>,
      )}

      {step(
        3,
        "¿Qué servicios realizaste?",
        doneCats.length > 0,
        <BlockStack gap="100">
          <Text tone="subdued">Marca solo lo que hiciste en esta visita.</Text>
          {categories.map((c) => (
            <div className="fc-check" key={c.key}>
              <Checkbox
                label={
                  <InlineStack gap="200" blockAlign="center">
                    <span>{c.label}</span>
                    {expected.has(c.key) && <Badge tone="info">Solicitado</Badge>}
                  </InlineStack>
                }
                helpText={c.hint || undefined}
                checked={doneCats.includes(c.key)}
                onChange={() => toggleCat(c.key)}
              />
            </div>
          ))}
        </BlockStack>,
      )}

      {step(
        4,
        "Fotos del trabajo",
        evidenceReady,
        <BlockStack gap="400">
          {!doneCats.length && (
            <Text tone="subdued">Primero selecciona los servicios realizados.</Text>
          )}
          {doneCatObjs.map((c) => {
            const extra = shotPhotos(c.key, "extra");
            return (
              <div className="fc-evidence" key={c.key}>
                <BlockStack gap="300">
                  <Text variant="headingSm" as="h4">
                    {c.label}
                  </Text>
                  {c.shots.map((sh) => {
                    const shots = shotPhotos(c.key, sh.key);
                    return (
                      <div className="fc-hint" key={sh.key}>
                        <InlineStack align="space-between" blockAlign="start" wrap={false} gap="200">
                          <InlineStack gap="200" blockAlign="start" wrap={false}>
                            <span className={`fc-dot ${shots.length ? "fc-dot-ok" : ""}`} />
                            <BlockStack gap="050">
                              <InlineStack gap="150" blockAlign="center">
                                <Text fontWeight="medium">{sh.label}</Text>
                                {sh.required ? (
                                  <Badge tone="attention" size="small">Obligatoria</Badge>
                                ) : (
                                  <Badge size="small">Opcional</Badge>
                                )}
                              </InlineStack>
                              {sh.hint && (
                                <Text tone="subdued" variant="bodySm">
                                  {sh.hint}
                                </Text>
                              )}
                            </BlockStack>
                          </InlineStack>
                          <PickPhoto
                            icon={CameraIcon}
                            accessibilityLabel={`Foto: ${sh.label}`}
                            onFiles={([f]) =>
                              upload(f, { kind: "evidence", category: c.key, shot: sh.key })
                            }
                          />
                        </InlineStack>
                        {shots.length > 0 && (
                          <div className="fc-thumbs">
                            {shots.map((p) => (
                              <Thumb key={p.path} photo={p} onDelete={removePhoto} />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {extra.length > 0 && (
                    <div className="fc-thumbs">
                      {extra.map((p) => (
                        <Thumb key={p.path} photo={p} onDelete={removePhoto} />
                      ))}
                    </div>
                  )}
                  <InlineStack>
                    <PickPhoto
                      icon={ImageIcon}
                      camera={false}
                      multiple
                      variant="plain"
                      onFiles={(files) =>
                        files.forEach((f) =>
                          upload(f, { kind: "evidence", category: c.key, shot: "extra" }),
                        )
                      }
                    >
                      Agregar otras fotos
                    </PickPhoto>
                  </InlineStack>
                  {c.requires_note && (
                    <TextField
                      label={c.note_prompt || "Descripción"}
                      multiline={2}
                      autoComplete="off"
                      value={catNotes[c.key] || ""}
                      onChange={(v) => setCatNotes((prev) => ({ ...prev, [c.key]: v }))}
                    />
                  )}
                </BlockStack>
              </div>
            );
          })}
        </BlockStack>,
      )}

      {step(
        5,
        "Notas y otras fotos (opcional)",
        false,
        <BlockStack gap="300">
          <TextField
            label="Comentarios"
            multiline={3}
            autoComplete="off"
            value={notes}
            onChange={setNotes}
            placeholder="Hallazgos, refacciones pendientes, recomendaciones…"
          />
          {photos.some((p) => p.kind === "other") && (
            <div className="fc-thumbs">
              {photos
                .filter((p) => p.kind === "other")
                .map((p) => (
                  <Thumb key={p.path} photo={p} onDelete={removePhoto} />
                ))}
            </div>
          )}
          <InlineStack gap="200">
            <PickPhoto icon={CameraIcon} onFiles={([f]) => upload(f, { kind: "other" })}>
              Cámara
            </PickPhoto>
            <PickPhoto
              icon={ImageIcon}
              camera={false}
              multiple
              onFiles={(files) => files.forEach((f) => upload(f, { kind: "other" }))}
            >
              Galería
            </PickPhoto>
          </InlineStack>
        </BlockStack>,
      )}

      {photos.some((p) => p.failed) && (
        <Banner tone="critical" title="Algunas fotos no se subieron">
          <p>Elimínalas (✕) y vuelve a tomarlas.</p>
        </Banner>
      )}
      {submitError && <Banner tone="critical">{submitError}</Banner>}

      <div className="fc-submit">
        {missing.length > 0 && (
          <Text tone="subdued" alignment="center">
            Falta: {missing.join(", ")}
          </Text>
        )}
        <Button
          variant="primary"
          size="large"
          fullWidth
          loading={submitting}
          disabled={missing.length > 0 || anyUploading}
          onClick={submit}
        >
          {anyUploading ? "Subiendo fotos…" : "Enviar servicio"}
        </Button>
      </div>
    </BlockStack>,
  );
}

export default FieldCapture;
